import { studioBookingSourceLabel, type StudioBookingSourceKey } from "@/lib/studio-data-filters";

export type AccountVertical = "fitness" | "short_term_rental";

const ACCOUNT_VERTICAL_VALUES: ReadonlySet<AccountVertical> = new Set([
  "fitness",
  "short_term_rental",
]);

/**
 * Normalizes incoming backend/account values to known vertical keys.
 * Slice 1 default is fitness to preserve current sports-first behavior.
 */
export function normalizeAccountVertical(value: unknown): AccountVertical {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (ACCOUNT_VERTICAL_VALUES.has(normalized as AccountVertical)) {
    return normalized as AccountVertical;
  }
  return "fitness";
}

export function isShortTermRentalVertical(vertical: unknown): boolean {
  return normalizeAccountVertical(vertical) === "short_term_rental";
}

export type VerticalLabels = {
  dataSectionNavLabel: string;
  dataSectionPageTitle: string;
  dataSectionReadOnlyNoun: string;
  /** Owner CTA: full phrase (empty state, hover-reveal on list + icon) */
  dataSectionAddUnitExpandedLabel: string;
  /** Left list column header (next to Add unit) */
  dataSectionListColumnTitle: string;
  /** Mobile-only subtitle under the data section title */
  dataSectionMobileDescription: string;
  /** Header switcher label when scope is all units */
  aggregateScopeLabel: string;
  /** CTA to open scope picker (overlay button, etc.) */
  selectScopeActionLabel: string;
  /** Default overlay when user must pick one unit */
  overlaySelectScopeMessage: string;
  teamInviteUnitsMulti: string;
  teamInviteUnitsSingle: string;
  teamInviteNoUnitsAvailable: string;
  teamInviteSelectAtLeastOne: string;
  /** Small caps section label in chat customer panel (identified) */
  chatsConversationUnitLabel: string;
  chatsIdentificationMatchingCopy: string;
  agentTraceSelectScopeMessage: string;
  agentTraceLoadErrorNeedsScope: string;
  agentTraceNoAuditEventsForUnit: string;
  aiSettingsContactOwnerReadOnly: string;
  /** Onboarding: booking / listing channel step */
  onboardingBookingConnectTitle: string;
  onboardingBookingConnectSubtitle: string;
  onboardingBookingUsernamePlaceholder: string;
  /** Dashboard stat cards */
  dashboardStatConversationsToday: string;
  dashboardStatHandledByAiPct: string;
  dashboardStatBookingsMade: string;
  dashboardStatAvgFirstResponse: string;
  /** Chats customer panel (mock / state copy) */
  chatsMockJoinedBookingLine: string;
  chatsMockMembershipHeading: string;
  chatsMockScheduleHeading: string;
  /** Customer info panel: platforms / booking channels block */
  chatsCustomerPanelPlatformsHeading: string;
  chatsCustomerPanelAggregatorLabel: string;
  chatsCustomerPanelNetworkLabel: string;
  chatsCustomerPanelPrimaryPlatformIdLabel: string;
  chatsCustomerPanelSecondaryPlatformIdLabel: string;
  chatsCustomerPanelAggregatorIdLabel: string;
  chatsCustomerPanelCustomerRecordIdLabel: string;
  chatsCustomerPanelConversationRefLabel: string;
  chatsUnmatchedNotFoundTitle: string;
  chatsUnmatchedNotFoundBody: string;
  chatsIdentificationInProgressTitle: string;
  chatsNoMembershipRecordTitle: string;
  chatsNoMembershipRecordBody: string;
  /** Header: opens agent trace panel */
  visionAgentTraceButtonLabel: string;
  /** In-page scope bar (dashboard / chats / data): explains studio vs property switcher */
  pageScopeDescription: string;
  /** Settings → Channels table: column for unit name */
  connectionsTableUnitColumn: string;
  /** Settings → Account: owner-only product vertical control */
  settingsAccountVerticalTitle: string;
  settingsAccountVerticalHint: string;
};

const LABELS_BY_VERTICAL: Record<AccountVertical, VerticalLabels> = {
  fitness: {
    dataSectionNavLabel: "Studio Data",
    dataSectionPageTitle: "Studio Data",
    dataSectionReadOnlyNoun: "Studio Data",
    dataSectionAddUnitExpandedLabel: "Add new studio",
    dataSectionListColumnTitle: "Studios",
    dataSectionMobileDescription:
      "Manage your studio information, FAQs, policies, and class schedule.",
    aggregateScopeLabel: "All Studios",
    selectScopeActionLabel: "Select Studio",
    overlaySelectScopeMessage: "Switch to a specific studio to edit settings.",
    teamInviteUnitsMulti: "Studios (select one or more)",
    teamInviteUnitsSingle: "Studio",
    teamInviteNoUnitsAvailable: "No studios are available for invitations with your account.",
    teamInviteSelectAtLeastOne: "Select at least one studio.",
    chatsConversationUnitLabel: "Studio",
    chatsIdentificationMatchingCopy:
      "We're matching this contact to a studio member. This may take a moment.",
    agentTraceSelectScopeMessage: "Select a studio to load traces.",
    agentTraceLoadErrorNeedsScope: "Select a studio and enter a valid agent run UUID.",
    agentTraceNoAuditEventsForUnit: "No audit events in this time window for this studio.",
    aiSettingsContactOwnerReadOnly: "Contact your studio owner to change AI settings.",
    onboardingBookingConnectTitle: "Connect Booking Platform",
    onboardingBookingConnectSubtitle:
      "Connect your booking platform to sync classes, schedules, and memberships.",
    onboardingBookingUsernamePlaceholder: "you@studio.com",
    dashboardStatConversationsToday: "Conversations today",
    dashboardStatHandledByAiPct: "Handled by AI %",
    dashboardStatBookingsMade: "Bookings made",
    dashboardStatAvgFirstResponse: "Avg first response",
    chatsMockJoinedBookingLine: "Joined Eversports",
    chatsMockMembershipHeading: "Membership",
    chatsMockScheduleHeading: "Classes",
    chatsCustomerPanelPlatformsHeading: "Platforms",
    chatsCustomerPanelAggregatorLabel: "Aggregator",
    chatsCustomerPanelNetworkLabel: "Network",
    chatsCustomerPanelPrimaryPlatformIdLabel: "Eversports customer ID",
    chatsCustomerPanelSecondaryPlatformIdLabel: "myClubs member ID",
    chatsCustomerPanelAggregatorIdLabel: "Aggregator ID",
    chatsCustomerPanelCustomerRecordIdLabel: "Customer ID",
    chatsCustomerPanelConversationRefLabel: "Conversation",
    chatsUnmatchedNotFoundTitle: "Customer not found in Eversports",
    chatsUnmatchedNotFoundBody:
      "This contact doesn't match any known member. Manual lookup may be required.",
    chatsIdentificationInProgressTitle: "Customer identification in progress",
    chatsNoMembershipRecordTitle: "No membership record",
    chatsNoMembershipRecordBody: "This looks like a general inquiry or potential new member.",
    visionAgentTraceButtonLabel: "Vision agent trace",
    pageScopeDescription:
      "Dashboard, chats, and studio data below follow the studio you select here. Use “All Studios” for an aggregate view where available.",
    connectionsTableUnitColumn: "Studio",
    settingsAccountVerticalTitle: "Product vertical",
    settingsAccountVerticalHint:
      "Fitness keeps studio and class wording. Short-term rental switches copy and listing-channel options to a property context.",
  },
  short_term_rental: {
    dataSectionNavLabel: "Property Data",
    dataSectionPageTitle: "Property Data",
    dataSectionReadOnlyNoun: "Property Data",
    dataSectionAddUnitExpandedLabel: "Add new property",
    dataSectionListColumnTitle: "Properties",
    dataSectionMobileDescription:
      "Manage your property information, FAQs, policies, and guest-facing details.",
    aggregateScopeLabel: "All Properties",
    selectScopeActionLabel: "Select Property",
    overlaySelectScopeMessage: "Switch to a specific property to edit settings.",
    teamInviteUnitsMulti: "Properties (select one or more)",
    teamInviteUnitsSingle: "Property",
    teamInviteNoUnitsAvailable: "No properties are available for invitations with your account.",
    teamInviteSelectAtLeastOne: "Select at least one property.",
    chatsConversationUnitLabel: "Property",
    chatsIdentificationMatchingCopy:
      "We're matching this contact to a guest or reservation. This may take a moment.",
    agentTraceSelectScopeMessage: "Select a property to load traces.",
    agentTraceLoadErrorNeedsScope: "Select a property and enter a valid agent run UUID.",
    agentTraceNoAuditEventsForUnit: "No audit events in this time window for this property.",
    aiSettingsContactOwnerReadOnly: "Contact your property owner to change AI settings.",
    onboardingBookingConnectTitle: "Connect listing channel",
    onboardingBookingConnectSubtitle:
      "Connect your listing or reservation platform to sync availability, reservations, and guest details.",
    onboardingBookingUsernamePlaceholder: "you@example.com",
    dashboardStatConversationsToday: "Conversations today",
    dashboardStatHandledByAiPct: "Handled by AI %",
    dashboardStatBookingsMade: "Reservations confirmed",
    dashboardStatAvgFirstResponse: "Avg first response",
    chatsMockJoinedBookingLine: "Primary booking channel",
    chatsMockMembershipHeading: "Guest program",
    chatsMockScheduleHeading: "Upcoming stays",
    chatsCustomerPanelPlatformsHeading: "Channels",
    chatsCustomerPanelAggregatorLabel: "Primary channel",
    chatsCustomerPanelNetworkLabel: "Partner",
    chatsCustomerPanelPrimaryPlatformIdLabel: "Guest ID",
    chatsCustomerPanelSecondaryPlatformIdLabel: "Reservation reference",
    chatsCustomerPanelAggregatorIdLabel: "Source reference",
    chatsCustomerPanelCustomerRecordIdLabel: "Guest record ID",
    chatsCustomerPanelConversationRefLabel: "Conversation",
    chatsUnmatchedNotFoundTitle: "Guest not found in booking system",
    chatsUnmatchedNotFoundBody:
      "This contact doesn't match a known guest or reservation. Manual lookup may be required.",
    chatsIdentificationInProgressTitle: "Guest identification in progress",
    chatsNoMembershipRecordTitle: "No reservation on file",
    chatsNoMembershipRecordBody: "This looks like a general inquiry or a potential new booking.",
    visionAgentTraceButtonLabel: "Vision agent trace",
    pageScopeDescription:
      "Dashboard, chats, and property data below follow the property you select here. Use “All Properties” for an aggregate view where available.",
    connectionsTableUnitColumn: "Property",
    settingsAccountVerticalTitle: "Product vertical",
    settingsAccountVerticalHint:
      "Short-term rental uses property and guest wording with listing-channel integrations. Fitness uses studio and class wording.",
  },
};

/** Base path for the data section (IA: STR uses `/property-data`, fitness uses `/studio-data`). */
export function getDataSectionBasePath(vertical: unknown): "/studio-data" | "/property-data" {
  return isShortTermRentalVertical(vertical) ? "/property-data" : "/studio-data";
}

export function getVerticalLabels(vertical: unknown): VerticalLabels {
  return LABELS_BY_VERTICAL[normalizeAccountVertical(vertical)];
}

/** Studio list row for `conversation.studioId` + `connections.bookingPlatform.status === "connected"`. */
export type ChatsCustomerPanelBookingContext = {
  bookingPlatformConnected: boolean;
  bookingSourceKey: StudioBookingSourceKey;
};

/**
 * Chats customer panel: do not name a booking/listing product unless that integration is connected.
 * @see Chats `CustomerInfoPanel` — uses `GET /studios` connection snapshot per conversation studio.
 */
export function getChatsCustomerPanelDynamicCopy(
  vertical: unknown,
  ctx: ChatsCustomerPanelBookingContext
): {
  joinedBookingLine: string;
  primaryExternalIdLabel: string;
  secondaryExternalIdLabel: string;
  unmatchedTitle: string;
} {
  const isStr = isShortTermRentalVertical(vertical);
  const base = getVerticalLabels(vertical);

  if (!ctx.bookingPlatformConnected) {
    return {
      joinedBookingLine: isStr ? "No listing channel connected" : "No booking platform connected",
      primaryExternalIdLabel: isStr ? base.chatsCustomerPanelPrimaryPlatformIdLabel : "Member / customer ID",
      secondaryExternalIdLabel: isStr ? base.chatsCustomerPanelSecondaryPlatformIdLabel : "Secondary reference",
      unmatchedTitle: isStr ? "No matching guest record" : "No matching customer record",
    };
  }

  const key = ctx.bookingSourceKey === "none" ? "other" : ctx.bookingSourceKey;
  const platformName = studioBookingSourceLabel(key);

  if (isStr) {
    return {
      joinedBookingLine: `${base.chatsMockJoinedBookingLine} · ${platformName}`,
      primaryExternalIdLabel: base.chatsCustomerPanelPrimaryPlatformIdLabel,
      secondaryExternalIdLabel: base.chatsCustomerPanelSecondaryPlatformIdLabel,
      unmatchedTitle: `Guest not found in ${platformName}`,
    };
  }

  return {
    joinedBookingLine: `Connected · ${platformName}`,
    primaryExternalIdLabel: key === "eversports" ? "Eversports customer ID" : `${platformName} customer ID`,
    secondaryExternalIdLabel:
      key === "mindbody" ? "Mindbody client ID" : key === "bsport" ? "Bsport member ID" : "Partner / network ID",
    unmatchedTitle: `Customer not found in ${platformName}`,
  };
}

const FITNESS_BOOKING_PLATFORMS = ["Eversports", "Mindbody", "Bsport", "FitogramPro"] as const;
const RENTAL_BOOKING_PLATFORMS = ["Airbnb", "Booking.com"] as const;

export function getBookingPlatformsForVertical(vertical: unknown): readonly string[] {
  const resolved = normalizeAccountVertical(vertical);
  if (resolved === "short_term_rental") {
    return RENTAL_BOOKING_PLATFORMS;
  }
  return FITNESS_BOOKING_PLATFORMS;
}

/**
 * Settings → Connections “Configure 2FA” gating (Eversports + fitness).
 * UX-40: onboarding wizard no longer inserts a dedicated 2FA step after FE-21
 * Authenticator-on-connect; keep this helper for Settings late-add only.
 */
export function requiresBooking2FA(platform: string, vertical: unknown): boolean {
  return normalizeAccountVertical(vertical) === "fitness" && platform === "Eversports";
}

/**
 * Client-local internal steps: 2=Booking, 4=Email, 5=Messaging, 6=Sync.
 * Step 3 (dedicated 2FA) removed by UX-40 — FE-21 stores Authenticator on connect;
 * skip path adds 2FA later via Settings → Connections.
 * UX-41: Combined Messaging (IG+WA) between Email and Sync.
 * Server onboarding complete still uses PUT step 4 action complete.
 */
export function getNextOnboardingStep(currentStep: number, _bookingPlatform: string, _vertical: unknown): number {
  if (currentStep === 2) return 4;
  if (currentStep === 3) return 4; // stale localStorage from pre-UX-40
  if (currentStep === 4) return 5; // Messaging
  if (currentStep === 5) return 6; // Sync
  return Math.min(6, currentStep + 1);
}

/**
 * Client-local internal steps: 2=Booking, 4=Email, 5=Messaging, 6=Sync (no wizard 2FA step).
 */
export function getPreviousOnboardingStep(currentStep: number, _bookingPlatform: string, _vertical: unknown): number {
  if (currentStep === 6) return 5;
  if (currentStep === 5) return 4;
  if (currentStep === 4) return 2;
  if (currentStep === 3) return 2; // stale localStorage from pre-UX-40
  return Math.max(2, currentStep - 1);
}

/** Visual progress index (0-based) for onboarding shell. Always 4 visual steps (UX-41). */
export function getOnboardingVisualStep(currentStep: number, _bookingPlatform: string, _vertical: unknown): number {
  if (currentStep <= 2) return 0;
  if (currentStep === 3 || currentStep === 4) return 1; // 3 = stale → treat as email
  if (currentStep === 5) return 2; // Messaging
  return 3; // Sync
}

export function getOnboardingTotalSteps(_bookingPlatform: string, _vertical: unknown): number {
  return 4;
}
